import { useEffect } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Operation } from '@/backend/models/operation';
import { I18n } from '@/i18n/I18n';
import { Play } from '@/frontend/SVGs/Play';
import { nativeBridge } from '@/frontend/embed/nativeBridge';
import { useCommandingRunStore } from '@/frontend/store/CommandingRunStore';
import { useRequiredConnections } from '@/frontend/hooks/useRequiredConnections';
import { Commanding } from '@/backend/models/commanding';

/**
 * The Run Automation button.
 *
 * The engine runs on the user's machine, so the button is disabled only when
 * there is no desktop app to reach it through. It does not ask whether the
 * engine is up: desktop starts the engine if it is not running, and a run that
 * still fails says why beneath the button.
 *
 * A run carries on in desktop when the page is left, so the first button to
 * mount asks desktop which runs are still going, and shows those as running.
 *
 * The framing check is synchronous and stays. Without it a click outside the
 * desktop app would `prepare` -- which deletes the commanding's previous runs --
 * before failing to reach the engine.
 *
 * A commanding addressing equipment cannot run until that equipment is
 * connected, so the button says which is missing rather than starting a run the
 * engine has nothing to route.
 */

export function RunAutomationControl(props: { operation: Operation; hasRun: boolean }) {
  const { operation, hasRun } = props;

  const framed = nativeBridge.isAvailable();
  const running = useCommandingRunStore((state) => Boolean(state.runningByOperationId[operation.id]));
  const stopping = useCommandingRunStore((state) => Boolean(state.runningByOperationId[operation.id]?.stopping));
  // Desktop has nothing to stop until it has the run going, and `stopRun` would
  // quietly do nothing before then.
  const stoppable = useCommandingRunStore((state) => Boolean(state.runningByOperationId[operation.id]?.started));
  const runError = useCommandingRunStore((state) => state.runErrorByOperationId[operation.id]);
  const runCommanding = useCommandingRunStore((state) => state.runCommanding);
  const stopRun = useCommandingRunStore((state) => state.stopRun);
  const reconnect = useCommandingRunStore((state) => state.reconnect);

  useEffect(() => { void reconnect(); }, [reconnect]);

  const commanding = operation.commanding as Commanding | undefined;
  // Input tasks only, as desktop's CommandingItemRun gates them.
  const missing = useRequiredConnections(commanding?.inputTasks);

  const label = hasRun ? I18n.get('rerunAutomation') : I18n.get('runAutomation');

  // Not while a run is going: desktop is driving it and the equipment it needed
  // was there when it started.
  const blocked = !running && missing.length > 0;

  if (!framed || blocked) {
    const reason = framed
      ? I18n.get('connectEquipmentToRun', { equipment: missing.map(({ name }) => name).join(', ') })
      : I18n.get('cannotRunAutomation');

    return (
      <OverlayTrigger
        placement="top"
        delay={{ show: 250, hide: 400 }}
        overlay={<Tooltip>{reason}</Tooltip>}
      >
        <div style={{ maxWidth: 'fit-content' }}><button
          aria-label={label}
          disabled
          type='button'
          className='btn d-block btn-success'>
            <div className='d-flex align-items-center gap-2'>
              <Play />
              {label}
            </div>
        </button></div>
      </OverlayTrigger>
    );
  }

  return (
    <div className='d-flex flex-column gap-1'>
      <div className='d-flex gap-2'>
        <button
          aria-label={label}
          disabled={running}
          onClick={() => void runCommanding(operation.id)}
          type='button'
          className='btn btn-success'>
            <div className='d-flex align-items-center gap-2'>
              { running
                ? <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true' />
                : <Play /> }
              {running ? I18n.get('runningAutomation') : label}
            </div>
        </button>
        { running &&
          <button
            aria-label={stopping ? I18n.get('stoppingAutomation') : I18n.get('stopAutomation')}
            disabled={stopping || !stoppable}
            onClick={() => void stopRun(operation.id)}
            type='button'
            className='btn btn-outline-danger'>
              {stopping ? I18n.get('stoppingAutomation') : I18n.get('stopAutomation')}
          </button>
        }
      </div>
      { runError && <small className='text-danger'>{runError}</small> }
    </div>
  );
}
